/**
 * Ported VERBATIM from apr_template `src/pages/IntegrationGroupings.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect } from 'react';
import PageHeader from '../compat/PageHeader';
import { authFetch } from '../compat/api';
import HelpPanel from '../compat/HelpPanel';
import { dispositionColor as matrixColor } from '../dispositionPalette';
import type { Help } from '../compat/helpTypes';
import './IntegrationApplicationMasterData.css';
// Decision Workspace design (owner ruling 2026-10-21): kit icons, panels and token pairs.
import { Icon, IconButton, Sheet, icons } from '@platform/ui-kit/workspace';
import { WS_CHIP, WS_COLOR, WS_RADIUS, WS_STATUS, wsTone, wsType, type WsTone } from '../workspace/ws';
import { useWsDialogs } from '../workspace/useWsDialogs';

const IntegrationGroupingsHelp: Help = {
  title: 'Integration Groupings',
  icon: '📦',
  purpose:
    'Organize dispositioned applications into <strong>integration groups</strong> &mdash; coordinated delivery waves that will be scheduled and resourced together on the roadmap.',
  doHere: [
    'On the <strong>Application Selection</strong> tab, tick apps and click <strong>Create Group</strong> to build a new wave.',
    'Use search and the recommendation filter, or <strong>Select All</strong> / <strong>Clear Selection</strong>.',
    'Name the group, set an <strong>Earliest Start Date</strong> and the <strong>Application Dependencies Exist</strong> flag.',
    'On the <strong>Integration Groups</strong> tab, use <strong>Add Apps</strong>, <strong>Edit</strong> or <strong>Delete</strong> to maintain existing groups.',
    'Flag a group as <em>Excl. Cost &amp; Benefit</em>, License Renegotiation, Consolidation or Cloud Migration as needed.',
  ],
  fits: 'Integration &rarr; Grouping &amp; Scope &rarr; Integration Groupings',
  firstTime:
    'Select a few related apps on the Selection tab, click Create Group, give it a name and start date, and save.',
  sections: [
    {
      type: 'defs',
      label: 'Assignment rules',
      items: [
        { term: 'Retire / Re-Platform', color: '#dc3545', def: 'Single assignment &mdash; can belong to only one group; disabled once assigned.' },
        { term: 'Retain / Re-Design', color: '#28a745', def: 'Can be shared across multiple groups, subject to a start-date constraint.' },
      ],
    },
  ],
  links: [
    { href: '/integration-framework/application-master-data', label: 'Integration App Master Data' },
    { href: '/integration-framework/application-dependencies', label: 'Application Dependencies' },
  ],
};

interface Application {
  application_id_code: string;
  application_name: string;
  technology_stack?: string;
  it_hardware_infrastructure?: string;
  recommendation: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain';
  application_dependencies?: string;
  integration_dependencies?: string;
}

interface IntegrationGroup {
  group_id: string;
  group_name: string;
  group_description?: string;
  applications?: string[]; // Array of application IDs (parsed from application_list)
  application_list?: string; // Database field - semicolon separated
  execution_order?: string; // Database field - comma separated
  execution_sequence?: string[]; // Parsed from execution_order
  earliest_start_date?: string;
  group_complexity?: 'High' | 'Medium' | 'Low';
  roadmap_grouping_status?: 'Single Disposition' | 'Integration Required';
  application_dependencies_exist?: 'YES' | 'NO';
  integration_dependencies_exist?: 'YES' | 'NO';
  created_date?: string;
  created_by?: string;
  // Additional UI fields for compatibility
  groupComposition?: 'retirement-only' | 'migration-only' | 'mixed';
  dependencyPattern?: 'standalone' | 'internal-dependencies' | 'external-dependencies' | 'foundation-apps';
  timingScenario?: 'immediate-start' | 'sequential-required' | 'parallel-possible' | 'external-timing';
  resourceRequirements?: 'specialized-skills' | 'generic-skills' | 'dedicated-teams' | 'shared-resources';
  businessComplexity?: 'simple' | 'complex' | 'regulatory';
  specializedSkills?: string[];
  estimatedDuration?: number;
  priority?: 'high' | 'medium' | 'low';
  exclude_from_analysis?: boolean;
  license_renegotiation?: boolean;
  consolidation_group?: boolean;
  cloud_migration?: boolean;
}

interface ScheduleRecord {
  application_id: string;
  integration_group_id: string;
  schedule_status: string;
  deployment_status?: string;
  planned_end_date?: string;
  phase_golive_end?: string;
}

const IntegrationGroupings: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<IntegrationGroup | null>(null);
  const [showAddAppsModal, setShowAddAppsModal] = useState(false);
  const [targetGroup, setTargetGroup] = useState<IntegrationGroup | null>(null);
  const [appsToAdd, setAppsToAdd] = useState<string[]>([]);
  const [removingApplications, setRemovingApplications] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [filterRecommendation, setFilterRecommendation] = useState('all');
  const [activeTab, setActiveTab] = useState<'selection' | 'groups'>('selection');
  const [groupHasDependencies, setGroupHasDependencies] = useState(false);
  const [groupHasScheduleRecords, setGroupHasScheduleRecords] = useState(false);
  
  // Form state for new/edit group
  const [groupForm, setGroupForm] = useState<Partial<IntegrationGroup>>({
    group_name: '',
    group_description: '',
    applications: [],
    application_dependencies_exist: 'YES',
    earliest_start_date: '',
    created_by: 'Current User'
  });

  // Restricted applications state
  const [restrictedApplications, setRestrictedApplications] = useState<string[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleRecord[]>([]);
  const { confirm, notify, dialogs } = useWsDialogs();

  useEffect(() => {
    loadApplications();
    loadIntegrationGroups();
    loadRestrictedApplications();
    loadScheduleData();
  }, []);

  const loadRestrictedApplications = async () => {
    try {
      const response = await authFetch('/api/integration-groups/restricted-applications');
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const restrictedIds = result.data.map((item: any) => item.application_id);
          setRestrictedApplications(restrictedIds);
        }
      }
    } catch (err) {
      console.error('Error loading restricted applications:', err);
    }
  };

  const loadScheduleData = async () => {
    try {
      const response = await authFetch('/api/roadmap-schedule');
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setScheduleData(result.data);
      } else {
        setScheduleData([]);
      }
    } catch (err: any) {
      console.error('Error loading schedule data:', err);
      setScheduleData([]);
    }
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/applications-master?limit=500');
      if (!response.ok) throw new Error('Failed to fetch applications');

      const data = await response.json();
      if (data.success) {
        setApplications(data.data);
      }
    } catch (err: any) {
      console.error('Error loading applications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/integration-groups');
      if (!response.ok) throw new Error('Failed to fetch integration groups');
      
      const data = await response.json();
      if (data.success) {
        setIntegrationGroups(data.data || []);
      }
    } catch (err: any) {
      console.error('Error loading integration groups:', err);
      setError(`Failed to load integration groups: ${err.message}`);
      setIntegrationGroups([]);
    }
  };

  // Get all applications that are already assigned to integration groups
  const getAssignedApplications = (): string[] => {
    const assigned: string[] = [];
    integrationGroups.forEach(group => {
      if (group.applications) {
        assigned.push(...group.applications);
      }
    });
    return assigned;
  };

  // Check if an application is already assigned to a group (excluding the group being edited)
  // Retire and Re-Platform: Can only belong to ONE group (single assignment)
  // Retain and Re-Design: Can belong to multiple groups
  const isApplicationAssigned = (appId: string): boolean => {
    // Find the application to check its recommendation
    const app = applications.find(a => a.application_id_code === appId);

    // If it's a Retain or Re-Design application, it can be selected for multiple groups
    if (app && (app.recommendation === 'Retain' || app.recommendation === 'Re-Design')) {
      return false; // Never consider Retain/Re-Design apps as "assigned" (always selectable)
    }

    // For Retire and Re-Platform applications, use the original logic (single group only)
    const assigned: string[] = [];
    integrationGroups.forEach(group => {
      // Skip the group being edited (if any)
      if (editingGroup && group.group_id === editingGroup.group_id) {
        return;
      }
      if (group.applications) {
        assigned.push(...group.applications);
      }
    });
    return assigned.includes(appId);
  };

  // Find which group an application is assigned to
  const getAssignedGroup = (appId: string): IntegrationGroup | null => {
    for (const group of integrationGroups) {
      // Skip the group being edited (if any)
      if (editingGroup && group.group_id === editingGroup.group_id) {
        continue;
      }
      if (group.applications && group.applications.includes(appId)) {
        return group;
      }
    }
    return null;
  };

  // Get the deployment end date (GoLive end date) for an application
  const getApplicationDeploymentEndDate = (appId: string): string | null => {
    const schedule = scheduleData.find(s => s.application_id === appId);
    if (schedule && schedule.phase_golive_end) {
      return schedule.phase_golive_end.split('T')[0]!; // Return date part only
    }
    return null;
  };

  // Get the minimum allowed start date for a set of applications
  // For Retain/Re-Design apps already in another group, the start date must be after their deployment end date
  const getMinimumStartDateForApplications = (appIds: string[]): string | null => {
    let maxDeploymentEndDate: string | null = null;

    appIds.forEach(appId => {
      const app = applications.find(a => a.application_id_code === appId);
      // Only check Retain and Re-Design apps that are already assigned to another group
      if (app && (app.recommendation === 'Retain' || app.recommendation === 'Re-Design')) {
        const assignedGroup = getAssignedGroup(appId);
        if (assignedGroup) {
          const deploymentEndDate = getApplicationDeploymentEndDate(appId);
          if (deploymentEndDate) {
            if (!maxDeploymentEndDate || deploymentEndDate > maxDeploymentEndDate) {
              maxDeploymentEndDate = deploymentEndDate;
            }
          }
        }
      }
    });

    return maxDeploymentEndDate;
  };

  // Get applications that have start date constraints (Retain/Re-Design in other groups)
  const getAppsWithStartDateConstraints = (appIds: string[]): { appId: string; appName: string; deploymentEndDate: string; groupName: string }[] => {
    const constraints: { appId: string; appName: string; deploymentEndDate: string; groupName: string }[] = [];

    appIds.forEach(appId => {
      const app = applications.find(a => a.application_id_code === appId);
      if (app && (app.recommendation === 'Retain' || app.recommendation === 'Re-Design')) {
        const assignedGroup = getAssignedGroup(appId);
        if (assignedGroup) {
          const deploymentEndDate = getApplicationDeploymentEndDate(appId);
          if (deploymentEndDate) {
            constraints.push({
              appId,
              appName: app.application_name,
              deploymentEndDate,
              groupName: assignedGroup.group_name
            });
          }
        }
      }
    });

    return constraints;
  };

  const handleApplicationToggle = (appId: string) => {
    // Find the application to check its recommendation
    const app = applications.find(a => a.application_id_code === appId);

    // For RETIRE and RE-PLATFORM applications, prevent selection if already assigned (single group only)
    if (app && (app.recommendation === 'Retire' || app.recommendation === 'Re-Platform') && isApplicationAssigned(appId)) {
      return; // Do nothing if RETIRE/RE-PLATFORM app is already assigned
    }

    // For RETAIN and RE-DESIGN applications, always allow selection (multiple groups allowed)
    setSelectedApplications(prev =>
      prev.includes(appId)
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleSelectAll = () => {
    const filteredApps = getFilteredApplications();
    // Only select applications that are not already assigned to groups
    const availableAppIds = filteredApps
      .filter(app => !isApplicationAssigned(app.application_id_code))
      .map(app => app.application_id_code);
    setSelectedApplications(availableAppIds);
  };

  const handleClearSelection = () => {
    setSelectedApplications([]);
  };

  const getFilteredApplications = () => {
    const filtered = applications.filter(app => {
      const matchesSearch = searchTerm === '' || 
        app.application_id_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.application_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFilter = filterRecommendation === 'all' || 
        app.recommendation === filterRecommendation;
      
      return matchesSearch && matchesFilter;
    });

    // Sort applications: unassigned first, assigned last
    return filtered.sort((a, b) => {
      const aAssigned = isApplicationAssigned(a.application_id_code);
      const bAssigned = isApplicationAssigned(b.application_id_code);
      
      // If assignment status is different, sort by assignment (unassigned first)
      if (aAssigned !== bAssigned) {
        return aAssigned ? 1 : -1; // unassigned (-1) comes before assigned (1)
      }
      
      // If same assignment status, maintain alphabetical order by application ID
      return a.application_id_code.localeCompare(b.application_id_code);
    });
  };

  const analyzeGroupComposition = (appIds: string[]): 'retirement-only' | 'migration-only' | 'mixed' => {
    const selectedApps = applications.filter(app => appIds.includes(app.application_id_code));
    const hasRetirement = selectedApps.some(app => app.recommendation === 'Retire');
    const hasMigration = selectedApps.some(app => 
      app.recommendation === 'Re-Platform' || app.recommendation === 'Re-Design'
    );
    
    if (hasRetirement && !hasMigration) return 'retirement-only';
    if (hasMigration && !hasRetirement) return 'migration-only';
    return 'mixed';
  };

  const checkExistingRelationships = (appIds: string[]) => {
    const relationships: { appId: string, appName: string, groups: { groupId: string, groupName: string }[] }[] = [];
    
    appIds.forEach(appId => {
      const app = applications.find(a => a.application_id_code === appId);
      const assignedGroups: { groupId: string, groupName: string }[] = [];
      
      integrationGroups.forEach(group => {
        if (group.applications && group.applications.includes(appId)) {
          assignedGroups.push({
            groupId: group.group_id,
            groupName: group.group_name
          });
        }
      });
      
      if (assignedGroups.length > 0) {
        relationships.push({
          appId: appId,
          appName: app?.application_name || appId,
          groups: assignedGroups
        });
      }
    });
    
    return relationships;
  };

  const handleCreateGroup = async () => {
    // Check for existing relationships
    const relationships = checkExistingRelationships(selectedApplications);
    console.log('Debug - Selected applications:', selectedApplications);
    console.log('Debug - Found relationships:', relationships);
    console.log('Debug - Integration groups:', integrationGroups);
    
    if (relationships.length > 0) {
      // Show warning modal with existing relationships
      // DW: the source's warning window becomes the kit confirm dialog; same title, content and buttons.
      console.log('Debug - Showing warning modal with relationships:', relationships);
      const proceed = await confirm(
        <div style={{ whiteSpace: 'normal' }}>
          <p>Selected applications: {selectedApplications.join(', ')}</p>
          <p>Found {relationships.length} existing relationships</p>
          {relationships.length > 0 ? (
            <div>
              <h4>Existing Relationships:</h4>
              {relationships.map((rel, index) => (
                <div key={index} style={{ marginBottom: '10px', padding: '10px', backgroundColor: WS_COLOR.surfaceSubtle, borderRadius: WS_RADIUS.field }}>
                  <strong>{rel.appId} - {rel.appName}</strong>
                  <div>Assigned to groups:</div>
                  {rel.groups.map((group, gIndex) => (
                    <div key={gIndex} style={{ marginLeft: '10px' }}>
                      • {group.groupId} - {group.groupName}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <p>No existing relationships found, but modal was triggered.</p>
          )}
        </div>,
        { title: 'Warning: Existing Group Relationships', confirmLabel: 'Continue Creating Group', tone: 'destructive' }
      );
      if (proceed) {
        proceedWithGroupCreation();
      }
    } else {
      // No existing relationships, proceed normally
      proceedWithGroupCreation();
    }
  };

  const proceedWithGroupCreation = () => {
    setGroupForm({
      group_name: '',
      group_description: '',
      applications: selectedApplications,
      application_dependencies_exist: 'YES',
      earliest_start_date: '',
      created_by: 'Current User'
    });
    setGroupHasDependencies(false); // New group has no dependencies
    setGroupHasScheduleRecords(false); // New group has no schedule records
    setShowCreateGroupModal(true);
  };

  const handleSaveGroup = async () => {
    try {
      if (!groupForm.group_name?.trim()) {
        notify('Group name is required', 'error');
        return;
      }

      // Validate start date constraint for Retain/Re-Design apps
      const minStartDate = getMinimumStartDateForApplications(groupForm.applications || []);
      if (minStartDate && groupForm.earliest_start_date) {
        if (groupForm.earliest_start_date < minStartDate) {
          const appsWithConstraints = getAppsWithStartDateConstraints(groupForm.applications || []);
          const constraintDetails = appsWithConstraints.map(c =>
            `${c.appId} completes deployment in "${c.groupName}" on ${c.deploymentEndDate}`
          ).join('\n');
          notify(`Start date cannot be before ${minStartDate}.\n\nThe following applications have deployment constraints:\n${constraintDetails}`, 'error');
          return;
        }
      }

      let payload: any = {
        group_name: groupForm.group_name,
        group_description: groupForm.group_description || '',
        applications: groupForm.applications || [],
        execution_sequence: groupForm.applications || [],
        earliest_start_date: groupForm.earliest_start_date || null,
        application_dependencies_exist: groupForm.application_dependencies_exist || 'YES',
        created_by: groupForm.created_by || 'Current User',
        exclude_from_analysis: groupForm.exclude_from_analysis || false,
        license_renegotiation: groupForm.license_renegotiation || false,
        consolidation_group: groupForm.consolidation_group || false,
        cloud_migration: groupForm.cloud_migration || false
      };

      if (editingGroup) {
        // Update existing group (roadmap_grouping_status is excluded from payload)
        const response = await authFetch(`/api/integration-groups/${editingGroup.group_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update group');
        }
      } else {
        // Create new group
        const response = await authFetch('/api/integration-groups', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create group');
        }
      }

      // Reload groups from database
      await loadIntegrationGroups();
      await loadRestrictedApplications(); // Reload restricted apps after group changes

      // Reset form
      setShowCreateGroupModal(false);
      setEditingGroup(null);
      setSelectedApplications([]);
      setGroupHasDependencies(false);
      setGroupHasScheduleRecords(false);
      setGroupForm({
        group_name: '',
        group_description: '',
        applications: [],
        application_dependencies_exist: 'YES',
        earliest_start_date: '',
        created_by: 'Current User'
      });

      notify(`Integration group ${editingGroup ? 'updated' : 'created'} successfully!`, 'success');
    } catch (err: any) {
      console.error('Error saving group:', err);
      notify(`Failed to save integration group: ${err.message}`, 'error');
    }
  };

  const handleEditGroup = async (group: IntegrationGroup) => {
    setEditingGroup(group);

    // Properly format the date for the input field (YYYY-MM-DD format)
    const formattedDate = group.earliest_start_date
      ? group.earliest_start_date.split('T')[0]
      : '';

    setGroupForm({
      ...group,
      applications: group.applications || [],
      earliest_start_date: formattedDate
    });

    // Check if this group has any application dependencies
    try {
      const response = await authFetch(`/api/application-dependencies?integration_group_id=${group.group_id}`);
      const result = await response.json();
      if (result.success && result.data && result.data.length > 0) {
        setGroupHasDependencies(true);
      } else {
        setGroupHasDependencies(false);
      }
    } catch (err) {
      console.error('Error checking dependencies:', err);
      setGroupHasDependencies(false);
    }

    // Check if this group has any roadmap schedule records
    try {
      const scheduleResponse = await authFetch('/api/roadmap-schedule');
      const scheduleResult = await scheduleResponse.json();
      if (scheduleResult.success && Array.isArray(scheduleResult.data)) {
        const groupScheduleRecords = scheduleResult.data.filter(
          (record: any) => record.integration_group_id === group.group_id
        );
        setGroupHasScheduleRecords(groupScheduleRecords.length > 0);
      } else {
        setGroupHasScheduleRecords(false);
      }
    } catch (err) {
      console.error('Error checking schedule records:', err);
      setGroupHasScheduleRecords(false);
    }

    setShowCreateGroupModal(true);
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      // First check if group has any roadmap schedule records
      const scheduleResponse = await authFetch('/api/roadmap-schedule');
      const scheduleResult = await scheduleResponse.json();

      if (scheduleResult.success && Array.isArray(scheduleResult.data)) {
        const groupScheduleRecords = scheduleResult.data.filter(
          (record: any) => record.integration_group_id === groupId
        );

        if (groupScheduleRecords.length > 0) {
          notify(`Cannot delete Integration Group: ${groupScheduleRecords.length} roadmap schedule record(s) exist for this group.`, 'error');
          return;
        }
      }

      // No schedule records, proceed with confirmation
      if (!(await confirm('Are you sure you want to delete this integration group?', { tone: 'destructive' }))) {
        return;
      }

      const response = await authFetch(`/api/integration-groups/${groupId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete group');
      }

      // Reload groups from database
      await loadIntegrationGroups();
      notify('Integration group deleted successfully!', 'success');
    } catch (err: any) {
      console.error('Error deleting group:', err);
      notify(`Failed to delete integration group: ${err.message}`, 'error');
    }
  };

  const handleAddAppsToGroup = (group: IntegrationGroup) => {
    setTargetGroup(group);
    setAppsToAdd([]);
    setShowAddAppsModal(true);
  };

  const handleAppToAddToggle = (appId: string) => {
    setAppsToAdd(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleSaveAdditionalApps = async () => {
    if (!targetGroup || appsToAdd.length === 0) {
      notify('Please select at least one application to add.', 'warning');
      return;
    }

    try {
      // First validate the application assignments against the target group
      const validationResponse = await authFetch('/api/integration-groups/validate-assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applications: appsToAdd,
          excludeGroupId: null,
          targetGroupId: targetGroup.group_id
        })
      });

      if (!validationResponse.ok) {
        throw new Error('Failed to validate application assignments');
      }

      const validationResult = await validationResponse.json();

      if (!validationResult.success || !validationResult.data.valid) {
        const restrictions = validationResult.data.restricted_applications || [];
        const errorMessages = restrictions.map((app: any) =>
          `${app.application_id}: ${app.conflicting_group.reason}`
        ).join('\n');

        notify(`Cannot add applications to this group:\n\n${errorMessages}`, 'error');
        return;
      }

      // Get current applications in the target group
      const currentApps = targetGroup.applications || [];
      // Combine with new applications
      const updatedApps = [...currentApps, ...appsToAdd];

      const response = await authFetch(`/api/integration-groups/${targetGroup.group_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...targetGroup, // Include all existing group properties
          applications: updatedApps,
          execution_sequence: updatedApps // Use same as applications for now
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add applications to group');
      }

      // Reload groups from database
      await loadIntegrationGroups();
      await loadRestrictedApplications(); // Reload restricted apps after group changes

      // Close modal and reset state
      setShowAddAppsModal(false);
      setTargetGroup(null);
      setAppsToAdd([]);

      notify(`Successfully added ${appsToAdd.length} application(s) to ${targetGroup.group_name}!`, 'success');
    } catch (err: any) {
      console.error('Error adding applications to group:', err);
      notify(`Failed to add applications: ${err.message}`, 'error');
    }
  };

  // Function to validate and remove application from group
  const handleRemoveApplication = async (groupId: string, applicationId: string) => {
    try {
      setRemovingApplications(prev => new Set(prev).add(applicationId));

      // Validate if application can be removed
      const validationResponse = await authFetch(`/api/integration-groups/validate-removal/${groupId}/${applicationId}`, {
        method: 'POST'
      });

      if (!validationResponse.ok) {
        throw new Error('Failed to validate application removal');
      }

      const validationResult = await validationResponse.json();

      if (!validationResult.success || !validationResult.data.canRemove) {
        notify(`Cannot remove application: ${validationResult.data.reason}`, 'error');
        return;
      }

      // If validation passes, remove from group form
      setGroupForm(prev => ({
        ...prev,
        applications: prev.applications?.filter(id => id !== applicationId) || []
      }));

      notify(`Application ${applicationId} removed from group successfully!`, 'success');
    } catch (error: any) {
      console.error('Error removing application:', error);
      notify(`Failed to remove application: ${error.message}`, 'error');
    } finally {
      setRemovingApplications(prev => {
        const newSet = new Set(prev);
        newSet.delete(applicationId);
        return newSet;
      });
    }
  };

  const getAvailableAppsForGroup = (group: IntegrationGroup) => {
    return applications.filter(app => {
      const appId = app.application_id_code;
      const isInCurrentGroup = group.applications?.includes(appId) || false;

      // Don't include apps already in the current group
      if (isInCurrentGroup) {
        return false;
      }

      // Check if app is restricted
      const isRestricted = restrictedApplications.includes(appId);

      if (isRestricted) {
        // If app is restricted, it cannot be added to any other group
        return false;
      }

      // For non-restricted apps, check normal assignment logic
      const assigned = getAssignedApplications();
      const isAssigned = assigned.includes(appId);

      // Allow if not assigned, or if editing and it's in current group
      return !isAssigned;
    });
  };


  // Disposition colors (from scoring thresholds database)
    // PORT NOTE: Disposition Matrix colours (§1.9.3 rule 3), not a hard-coded palette.
const getRecommendationColor = (recommendation: string) => {
    return ['Retire', 'Re-Platform', 'Re-Design', 'Retain'].includes(recommendation) ? matrixColor(recommendation).base : WS_STATUS.neutral.fill;
  };

  // Helper function to get deployment status color
  // DW: each status maps to a kit status pair instead of a hand-picked fill/ink.
  const getDeploymentStatusTone = (status?: string): WsTone => {
    switch (status) {
      case 'Approved': return 'success';
      case 'In Development': return 'info';
      case 'Deployed': return 'success';
      case 'On Hold': return 'warning';
      case 'Planned': return 'neutral';
      default: return 'neutral';
    }
  };

  // Get deployment status for an application in a specific group
  const getApplicationDeploymentStatus = (appId: string, groupId: string): string | null => {
    const schedule = scheduleData.find(s => s.application_id === appId && s.integration_group_id === groupId);
    return schedule?.deployment_status || null;
  };

  const getGroupCompositionLabel = (composition: string): React.ReactNode => {
    switch (composition) {
      case 'retirement-only': return <><Icon icon={icons.Trash2} size={16} /> Retirement Only</>;
      case 'migration-only': return <><Icon icon={icons.RefreshCw} size={16} /> Migration Only</>;
      case 'mixed': return <><Icon icon={icons.Shuffle} size={16} /> Mixed Group</>;
      default: return composition;
    }
  };

  const getTimingScenarioLabel = (timing: string): React.ReactNode => {
    switch (timing) {
      case 'immediate-start': return <><Icon icon={icons.Rocket} size={16} /> Immediate Start</>;
      case 'sequential-required': return <><Icon icon={icons.ClipboardList} size={16} /> Sequential Required</>;
      case 'parallel-possible': return <><Icon icon={icons.Zap} size={16} /> Parallel Possible</>;
      case 'external-timing': return <><Icon icon={icons.Clock} size={16} /> External Timing</>;
      default: return timing;
    }
  };

  const closeGroupSheet = () => {
    setShowCreateGroupModal(false);
    setEditingGroup(null);
    setGroupHasDependencies(false);
    setGroupHasScheduleRecords(false);
  };

  const closeAddAppsSheet = () => {
    setShowAddAppsModal(false);
    setTargetGroup(null);
    setAppsToAdd([]);
  };

  // Shared inline type roles for this screen (kit scale).
  const fieldLabel: React.CSSProperties = { display: 'block', marginBottom: '4px', ...wsType('label'), fontWeight: 600 };
  const summaryLabel: React.CSSProperties = { ...wsType('body'), fontWeight: 600, color: WS_COLOR.textMuted };
  const lockedNote: React.CSSProperties = { ...wsType('meta'), color: WS_STATUS.error.ink, marginTop: '4px', display: 'block' };
  const lockedField: React.CSSProperties = { background: WS_COLOR.surfaceSubtle, color: WS_COLOR.textMuted, cursor: 'not-allowed' };
  const flagBox = (tone: WsTone): React.CSSProperties => ({ padding: '12px', ...wsTone(tone), borderRadius: WS_RADIUS.field });
  const flagLabel: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', ...wsType('table'), fontWeight: 500 };
  const flagNote: React.CSSProperties = { margin: '4px 0 0 26px', ...wsType('meta'), color: WS_COLOR.textMuted };
  const flagCheckbox: React.CSSProperties = { width: '18px', height: '18px', cursor: 'pointer' };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-header__title">Loading...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {dialogs}
      <HelpPanel help={IntegrationGroupingsHelp} />
      {/* Header */}
      <PageHeader title={<>Integration Groupings</>} subtitle={<>Select and organize applications into integration groups for roadmap implementation planning</>} />
      <div className="page-header__actions" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
<button
              onClick={() => setActiveTab('selection')}
              className={activeTab === 'selection' ? 'btn btn-primary' : 'btn'}
            >
              Application Selection
            </button>
            <button
              onClick={() => setActiveTab('groups')}
              className={activeTab === 'groups' ? 'btn btn-primary' : 'btn'}
            >
              Integration Groups ({integrationGroups.length})
            </button>
      </div>

      <div className="master-data-container">
        {activeTab === 'selection' ? (
          <>
            {/* Search and Filter Bar */}
            <div className="md-card">
              <div className="md-card-content">
                <div className="md-filters-row">
                  <div className="md-search-field">
                    <div className="md-input-group">
                      <span className="md-input-icon"><Icon icon={icons.Search} size={16} /></span>
                      <input
                        type="text"
                        placeholder="Search applications..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="md-input"
                      />
                    </div>
                  </div>

                  <div className="md-filter-field">
                    <select
                      value={filterRecommendation}
                      onChange={(e) => setFilterRecommendation(e.target.value)}
                      className="md-select"
                    >
                      <option value="all">All Recommendations</option>
                      <option value="Retire">Retire</option>
                      <option value="Re-Platform">Re-Platform</option>
                      <option value="Re-Design">Re-Design</option>
                      <option value="Retain">Retain</option>
                    </select>
                  </div>

                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleSelectAll}
                      className="btn"
                    >
                      Select All
                    </button>
                    <button
                      onClick={handleClearSelection}
                      className="btn"
                    >
                      Clear Selection
                    </button>
                    <button
                      onClick={handleCreateGroup}
                      disabled={selectedApplications.length === 0}
                      className="btn btn-primary"
                    >
                      Create Group ({selectedApplications.length} selected)
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Applications Summary */}
            {selectedApplications.length > 0 && (
              <div className="md-card" style={{ marginBottom: '16px' }}>
                <div className="md-card-content">
                  <h3 style={{ marginBottom: '12px' }}>Selected Applications Analysis</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                      <strong>Group Composition:</strong> {getGroupCompositionLabel(analyzeGroupComposition(selectedApplications))}
                    </div>
                    <div>
                      <strong>Total Selected:</strong> {selectedApplications.length} applications
                    </div>
                    <div>
                      <strong>Retirements:</strong> {applications.filter(app => selectedApplications.includes(app.application_id_code) && app.recommendation === 'Retire').length}
                    </div>
                    <div>
                      <strong>Migrations:</strong> {applications.filter(app => selectedApplications.includes(app.application_id_code) && (app.recommendation === 'Re-Platform' || app.recommendation === 'Re-Design')).length}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Legend for Application Selection */}
            <div className="md-card" style={{ marginBottom: '16px', ...wsTone('success') }}>
              <div className="md-card-content" style={{ padding: '12px' }}>
                <h4 style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px', ...wsType('label'), fontWeight: 600 }}>
                  <Icon icon={icons.ClipboardList} size={16} /> Application Selection Guide
                </h4>
                <div style={{ display: 'flex', gap: '24px', ...wsType('meta'), flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: WS_STATUS.success.ink, border: `2px solid ${WS_STATUS.success.ink}` }}></div>
                    <span><strong>Retain / Re-Design</strong> - Can belong to multiple groups (start date constraint applies)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: WS_STATUS.error.ink, border: `1px solid ${WS_STATUS.error.ink}` }}></div>
                    <span><strong>Retire / Re-Platform</strong> - Can only belong to one group (disabled when assigned)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Applications Table */}
            <div className="md-card">
              <div className="md-table-container">
                <table className="md-table">
                  <thead>
                    <tr>
                      <th className="md-table-header" style={{ width: '50px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={(() => {
                            const availableApps = getFilteredApplications().filter(app => !isApplicationAssigned(app.application_id_code));
                            return selectedApplications.length === availableApps.length && availableApps.length > 0;
                          })()}
                          onChange={() => {
                            const availableApps = getFilteredApplications().filter(app => !isApplicationAssigned(app.application_id_code));
                            if (selectedApplications.length === availableApps.length) {
                              handleClearSelection();
                            } else {
                              handleSelectAll();
                            }
                          }}
                        />
                      </th>
                      <th className="md-table-header" style={{ textAlign: 'left' }}>Application</th>
                      <th className="md-table-header" style={{ textAlign: 'center' }}>Technology</th>
                      <th className="md-table-header" style={{ textAlign: 'center' }}>Infrastructure</th>
                      <th className="md-table-header" style={{ textAlign: 'center' }}>Recommendation</th>
                      <th className="md-table-header" style={{ textAlign: 'center' }}>App Dependencies</th>
                      <th className="md-table-header" style={{ textAlign: 'center' }}>Assigned Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredApplications().map(app => {
                      const isAssigned = isApplicationAssigned(app.application_id_code);
                      const isSelected = selectedApplications.includes(app.application_id_code);
                      const isSingleGroupOnly = app.recommendation === 'Retire' || app.recommendation === 'Re-Platform';
                      const canMultipleGroups = app.recommendation === 'Retain' || app.recommendation === 'Re-Design';

                      return (
                        <tr
                          key={app.application_id_code}
                          className="md-table-row"
                          style={{
                            ...(isSelected ? wsTone('info') : { backgroundColor: 'transparent' }),
                            opacity: isAssigned && isSingleGroupOnly ? 0.5 : 1, // Dim RETIRE/RE-PLATFORM apps when assigned
                            ...(isAssigned && isSingleGroupOnly ? { color: WS_COLOR.textMuted } : null),
                            border: canMultipleGroups ? `1px solid ${WS_STATUS.success.ink}` : 'none', // Green border for multi-group apps
                            borderLeft: canMultipleGroups ? `4px solid ${WS_STATUS.success.ink}` : 'none' // Thick green left border
                          }}
                        >
                          <td className="md-table-cell" style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isAssigned && isSingleGroupOnly} // Disable RETIRE/RE-PLATFORM apps when assigned
                              onChange={() => handleApplicationToggle(app.application_id_code)}
                              style={{ cursor: (isAssigned && isSingleGroupOnly) ? 'not-allowed' : 'pointer' }}
                            />
                          </td>
                        <td className="md-table-cell">
                          <div className="md-app-info">
                            <div className="md-app-info__primary">{app.application_id_code}</div>
                            <div className="md-app-info__primary">{app.application_name}</div>
                          </div>
                        </td>
                        <td className="md-table-cell" style={{ textAlign: 'center' }}>
                          <span className="technology-info">
                            {app.technology_stack || 'Not specified'}
                          </span>
                        </td>
                        <td className="md-table-cell" style={{ textAlign: 'center' }}>
                          <span className="infrastructure-info">
                            {app.it_hardware_infrastructure || 'Not specified'}
                          </span>
                        </td>
                        <td className="md-table-cell" style={{ textAlign: 'center' }}>
                          <div
                            className="md-chip"
                            style={{
                              backgroundColor: getRecommendationColor(app.recommendation),
                              color: matrixColor(app.recommendation).onBase
                            }}
                          >
                            {app.recommendation}
                          </div>
                        </td>
                        <td className="md-table-cell" style={{ textAlign: 'center' }}>
                          {app.application_dependencies === 'YES' ? (
                            <span style={{ ...WS_CHIP, ...wsTone('info') }}>
                              <Icon icon={icons.Check} size={16} /> YES
                            </span>
                          ) : (
                            <span style={{ ...WS_CHIP, ...wsTone('neutral') }}>
                              <Icon icon={icons.Minus} size={16} /> NO
                            </span>
                          )}
                        </td>
                        <td className="md-table-cell" style={{ textAlign: 'center' }}>
                          {(() => {
                            const assignedGroup = getAssignedGroup(app.application_id_code);
                            return assignedGroup ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                <strong style={{ ...wsType('meta'), fontWeight: 600, color: WS_COLOR.textMuted }}>{assignedGroup.group_id}</strong>
                                <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{assignedGroup.group_name}</div>
                              </div>
                            ) : (
                              <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>-</span>
                            );
                          })()}
                        </td>
                      </tr>
                  );
                })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Integration Groups View */}
            <div className="md-card" style={{ marginBottom: '16px' }}>
              <div className="md-card-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h3 style={{ margin: 0 }}>Integration Groups Overview</h3>
                    <p style={{ color: WS_COLOR.textMuted, ...wsType('table'), margin: '4px 0 0' }}>
                      Manage and configure integration groups for roadmap implementation
                    </p>
                  </div>
                  <input
                    type="text"
                    placeholder="Search by application ID or name..."
                    value={groupSearchTerm}
                    onChange={(e) => setGroupSearchTerm(e.target.value)}
                    style={{ minWidth: '280px', ...(groupSearchTerm ? { borderColor: WS_COLOR.primary } : null) }}
                  />
                </div>
                {groupSearchTerm && (
                  <div style={{ marginTop: '8px', ...wsType('label'), color: WS_COLOR.textMuted, display: 'flex', alignItems: 'center' }}>
                    {integrationGroups.filter(g => {
                      const s = groupSearchTerm.toLowerCase();
                      const appIds = (g.application_list || '').split(';').map((id: string) => id.trim());
                      const groupApps = applications.filter(a => appIds.includes(a.application_id_code));
                      return g.group_name.toLowerCase().includes(s) ||
                        g.group_id.toLowerCase().includes(s) ||
                        appIds.some((id: string) => id.toLowerCase().includes(s)) ||
                        groupApps.some(a => a.application_name.toLowerCase().includes(s));
                    }).length} of {integrationGroups.length} groups
                    <button onClick={() => setGroupSearchTerm('')} className="btn" style={{ marginLeft: '8px' }}>Clear</button>
                  </div>
                )}
              </div>
            </div>

            {integrationGroups.length === 0 ? (
              <div className="md-card">
                <div className="md-empty-state">
                  <div className="md-empty-icon"><Icon icon={icons.Package} size={24} /></div>
                  <h3>No Integration Groups</h3>
                  <p>Switch to Application Selection tab to create your first integration group</p>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {integrationGroups.filter(group => {
                  if (!groupSearchTerm.trim()) return true;
                  const s = groupSearchTerm.toLowerCase();
                  const appIds = (group.application_list || '').split(';').map((id: string) => id.trim());
                  const groupApps = applications.filter(a => appIds.includes(a.application_id_code));
                  return group.group_name.toLowerCase().includes(s) ||
                    group.group_id.toLowerCase().includes(s) ||
                    appIds.some((id: string) => id.toLowerCase().includes(s)) ||
                    groupApps.some(a => a.application_name.toLowerCase().includes(s));
                }).map(group => (
                  <div key={group.group_id} className="md-card">
                    <div className="md-card-content">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                        <div style={{ textAlign: 'left' }}>
                          <h3 style={{ margin: '0 0 8px 0', textAlign: 'left' }}>
                            {group.group_id} - {group.group_name}
                          </h3>
                          <p style={{ color: WS_COLOR.textMuted, ...wsType('table'), margin: 0, textAlign: 'left' }}>{group.group_description}</p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleAddAppsToGroup(group)}
                            className="btn"
                          >
                            Add Apps
                          </button>
                          <button
                            onClick={() => handleEditGroup(group)}
                            className="btn"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteGroup(group.group_id)}
                            className="btn"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Single line summary */}
                      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: WS_COLOR.surfaceSubtle, borderRadius: WS_RADIUS.field, ...wsType('body') }}>
                        <strong style={summaryLabel}>Applications:</strong> {group.applications?.length || 0} apps | {' '}
                        <strong style={summaryLabel}>Start Date:</strong> {group.earliest_start_date ? group.earliest_start_date.split('T')[0] : 'TBD'} | {' '}
                        <strong style={summaryLabel}>Created:</strong> {group.created_date ? group.created_date.split('T')[0] : 'Unknown'} | {' '}
                        <strong style={summaryLabel}>App Dependencies:</strong>
                        <span style={{ ...WS_CHIP, ...wsTone(group.application_dependencies_exist === 'YES' ? 'warning' : 'success'), margin: '0 4px' }}>
                          {group.application_dependencies_exist || 'YES'}
                        </span>
                        {group.exclude_from_analysis && (
                          <>
                            {' | '}
                            <span style={{ ...WS_CHIP, ...wsTone('error'), marginLeft: '4px' }}>
                              Excl. Cost & Benefit
                            </span>
                          </>
                        )}
                      </div>

                      <div style={{ borderTop: `1px solid ${WS_COLOR.border}`, paddingTop: '12px' }}>
                        <strong style={summaryLabel}>Group Applications:</strong>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
                          {group.applications?.map(appId => {
                            const app = applications.find(a => a.application_id_code === appId);
                            if (!app) return null;
                            const deploymentStatus = getApplicationDeploymentStatus(appId, group.group_id);
                            return (
                              <div
                                key={appId}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  padding: '8px 12px',
                                  backgroundColor: WS_COLOR.surfaceSubtle,
                                  borderRadius: WS_RADIUS.field,
                                  border: `1px solid ${WS_COLOR.border}`,
                                  minWidth: '140px'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                  <span style={{ ...wsType('table'), fontWeight: 600, color: matrixColor(app.recommendation).ink }}>{appId}</span>
                                  <div
                                    className="md-chip"
                                    style={{
                                      backgroundColor: getRecommendationColor(app.recommendation),
                                      color: matrixColor(app.recommendation).onBase
                                    }}
                                  >
                                    {app.recommendation}
                                  </div>
                                </div>
                                <span style={{ ...WS_CHIP, ...wsTone(getDeploymentStatusTone(deploymentStatus || undefined)) }}>
                                  {deploymentStatus || 'Not Scheduled'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Group — a right-side panel (Decision Workspace Sheet; the source drawer was 900px, so double width). */}
      <Sheet
        open={showCreateGroupModal}
        title={editingGroup ? 'Edit Integration Group' : 'Create Integration Group'}
        onClose={closeGroupSheet}
        width={1062}
        actions={
          <>
            <button type="button" className="btn" onClick={closeGroupSheet}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveGroup}
              className="btn btn-primary"
              disabled={!groupForm.group_name}
            >
              {editingGroup ? 'Update Group' : 'Create Group'}
            </button>
          </>
        }
      >
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={fieldLabel}>Group Name *</label>
                <input
                  type="text"
                  value={groupForm.group_name}
                  onChange={(e) => setGroupForm({ ...groupForm, group_name: e.target.value })}
                  style={{ width: '100%' }}
                  placeholder="e.g., Phase 1 - Legacy Retirement"
                />
              </div>

              <div>
                <label style={fieldLabel}>Description</label>
                <textarea
                  value={groupForm.group_description}
                  onChange={(e) => setGroupForm({ ...groupForm, group_description: e.target.value })}
                  style={{ width: '100%', minHeight: '80px' }}
                  placeholder="Describe the purpose and scope of this integration group..."
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={fieldLabel}>Earliest Start Date</label>
                  {(() => {
                    const minStartDate = getMinimumStartDateForApplications(groupForm.applications || []);
                    const appsWithConstraints = getAppsWithStartDateConstraints(groupForm.applications || []);
                    const isDateLocked = editingGroup && groupHasScheduleRecords;
                    return (
                      <>
                        <input
                          type="date"
                          value={groupForm.earliest_start_date || ''}
                          min={minStartDate || undefined}
                          onChange={(e) => setGroupForm({ ...groupForm, earliest_start_date: e.target.value })}
                          disabled={!!isDateLocked}
                          style={{
                            width: '100%',
                            ...(isDateLocked
                              ? lockedField
                              : minStartDate
                                ? { borderColor: WS_STATUS.warning.ink, backgroundColor: WS_STATUS.warning.fill, cursor: 'pointer' }
                                : { cursor: 'pointer' })
                          }}
                        />
                        {isDateLocked && (
                          <small style={lockedNote}>
                            Cannot be changed - roadmap schedule records exist for this group
                          </small>
                        )}
                        {appsWithConstraints.length > 0 && (
                          <div style={{
                            marginTop: '8px',
                            padding: '8px 12px',
                            ...wsTone('warning'),
                            borderRadius: WS_RADIUS.field,
                            ...wsType('meta')
                          }}>
                            <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
                              <Icon icon={icons.AlertTriangle} size={16} /> Start Date Constraint:
                            </strong>
                            <div style={{ marginTop: '4px' }}>
                              {appsWithConstraints.map((c, idx) => (
                                <div key={idx}>
                                  <strong>{c.appId}</strong> must complete deployment in "{c.groupName}" by {c.deploymentEndDate}
                                </div>
                              ))}
                            </div>
                            <div style={{ marginTop: '4px', fontWeight: 600 }}>
                              Minimum start date: {minStartDate}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div>
                  <label style={fieldLabel}>Application Dependencies Exist</label>
                  <select
                    value={groupForm.application_dependencies_exist}
                    onChange={(e) => setGroupForm({ ...groupForm, application_dependencies_exist: e.target.value as 'YES' | 'NO' })}
                    className="md-select"
                    style={(editingGroup && groupHasDependencies) ? lockedField : { cursor: 'pointer' }}
                    disabled={!!(editingGroup && groupHasDependencies)}
                    title={editingGroup && groupHasDependencies ? "Cannot be changed when application dependencies exist for this group" : ""}
                  >
                    <option value="YES">YES</option>
                    <option value="NO">NO</option>
                  </select>
                  {editingGroup && groupHasDependencies && (
                    <small style={lockedNote}>
                      Cannot be changed - application dependencies exist for this group
                    </small>
                  )}
                </div>
              </div>

              {/* Exclusion Flag */}
              <div style={flagBox('warning')}>
                <label style={{ ...flagLabel, fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={groupForm.exclude_from_analysis || false}
                    onChange={(e) => setGroupForm({ ...groupForm, exclude_from_analysis: e.target.checked })}
                    style={flagCheckbox}
                  />
                  Not relevant for Disposition Cost & Benefit Realization
                </label>
              </div>

              {/* License Renegotiation Flag */}
              <div style={flagBox('warning')}>
                <label style={flagLabel}>
                  <input
                    type="checkbox"
                    checked={groupForm.license_renegotiation || false}
                    onChange={(e) => setGroupForm({ ...groupForm, license_renegotiation: e.target.checked })}
                    style={flagCheckbox}
                  />
                  License Renegotiation Group
                </label>
                <p style={flagNote}>
                  This group contains applications eligible for license cost renegotiation
                </p>
              </div>

              {/* Consolidation Group Flag */}
              {/* DW: the source's purple has no kit pair; neutral is the nearest non-status tone. */}
              <div style={flagBox('neutral')}>
                <label style={flagLabel}>
                  <input
                    type="checkbox"
                    checked={groupForm.consolidation_group || false}
                    onChange={(e) => setGroupForm({ ...groupForm, consolidation_group: e.target.checked })}
                    style={flagCheckbox}
                  />
                  Consolidation Group
                </label>
                <p style={flagNote}>
                  This group contains applications being consolidated into other applications
                </p>
              </div>

              {/* Cloud Migration Flag */}
              <div style={flagBox('info')}>
                <label style={flagLabel}>
                  <input
                    type="checkbox"
                    checked={groupForm.cloud_migration || false}
                    onChange={(e) => setGroupForm({ ...groupForm, cloud_migration: e.target.checked })}
                    style={flagCheckbox}
                  />
                  Cloud Migration Group
                </label>
                <p style={flagNote}>
                  This group contains applications migrating from on-premise to cloud
                </p>
              </div>

              <div>
                <label style={fieldLabel}>
                  Selected Applications ({groupForm.applications?.length || 0})
                </label>
                <div style={{
                  border: `1px solid ${WS_COLOR.border}`,
                  borderRadius: WS_RADIUS.field,
                  padding: '8px',
                  maxHeight: '150px',
                  overflowY: 'auto'
                }}>
                  {groupForm.applications?.map(appId => {
                    const app = applications.find(a => a.application_id_code === appId);
                    const isRemoving = removingApplications.has(appId);
                    return (
                      <div
                        key={appId}
                        style={{
                          ...wsType('body'),
                          margin: '4px',
                          color: WS_COLOR.text,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 12px',
                          border: `1px solid ${WS_COLOR.border}`,
                          borderRadius: WS_RADIUS.field,
                          backgroundColor: WS_COLOR.surfaceSubtle
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {appId}
                          {app && (
                            <div
                              className="md-chip"
                              style={{
                                backgroundColor: getRecommendationColor(app.recommendation),
                                color: matrixColor(app.recommendation).onBase
                              }}
                            >
                              {app.recommendation}
                            </div>
                          )}
                        </span>
                        {editingGroup && (
                          <IconButton
                            icon={isRemoving ? icons.Loader2 : icons.XCircle}
                            label={isRemoving ? 'Removing...' : 'Remove application from group'}
                            title={isRemoving ? 'Removing...' : 'Remove application from group'}
                            onClick={() => handleRemoveApplication(editingGroup.group_id, appId)}
                            disabled={isRemoving}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
      </Sheet>

      {/* Add Applications — a right-side panel (Decision Workspace Sheet, single width). */}
      <Sheet
        open={showAddAppsModal && !!targetGroup}
        title={`Add Applications to ${targetGroup?.group_name ?? ''}`}
        onClose={closeAddAppsSheet}
        width={531}
        actions={
          <>
            <button type="button" className="btn" onClick={closeAddAppsSheet}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveAdditionalApps}
              disabled={appsToAdd.length === 0}
              className="btn btn-primary"
            >
              Add {appsToAdd.length} Application(s)
            </button>
          </>
        }
      >
        {targetGroup && (
          <>
            <p style={{ color: WS_COLOR.textMuted, marginBottom: '20px', ...wsType('table') }}>
              Select applications to add to this integration group.
            </p>

            {getAvailableAppsForGroup(targetGroup).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: WS_COLOR.textMuted }}>
                <div style={{ marginBottom: '16px' }}><Icon icon={icons.Package} size={24} /></div>
                <h3>No Available Applications</h3>
                <p>No applications available. All applications are either already assigned to groups or already in this group.</p>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <strong>Available Applications ({getAvailableAppsForGroup(targetGroup).length}):</strong>
                </div>

                <div style={{ maxHeight: '300px', overflowY: 'auto', border: `1px solid ${WS_COLOR.border}`, borderRadius: WS_RADIUS.field }}>
                  {getAvailableAppsForGroup(targetGroup).map(app => (
                    <div
                      key={app.application_id_code}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        borderBottom: `1px solid ${WS_COLOR.border}`,
                        ...(appsToAdd.includes(app.application_id_code) ? wsTone('info') : { backgroundColor: WS_COLOR.surface })
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={appsToAdd.includes(app.application_id_code)}
                        onChange={() => handleAppToAddToggle(app.application_id_code)}
                        style={{ marginRight: '12px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ ...wsType('table'), fontWeight: 600 }}>{app.application_id_code}</div>
                        <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{app.application_name}</div>
                      </div>
                      <div>
                        <div
                          className="md-chip"
                          style={{
                            backgroundColor: getRecommendationColor(app.recommendation),
                            color: matrixColor(app.recommendation).onBase
                          }}
                        >
                          {app.recommendation}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {appsToAdd.length > 0 && (
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: WS_COLOR.surfaceSubtle, borderRadius: WS_RADIUS.field }}>
                    <strong>Selected: {appsToAdd.length} application(s)</strong>
                    <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: '4px' }}>
                      {appsToAdd.join(', ')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Sheet>
    </div>
  );
};

export default IntegrationGroupings;
